import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import {
  UsersThree, Buildings, CircleNotch, ArrowRight, Brain, WarningCircle,
} from "@phosphor-icons/react";

export default function ClientHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [access, setAccess] = useState(null);

  useEffect(() => {
    api.get("/my-access").then((r) => setAccess(r.data)).catch(() => setAccess({ groups: [], clients: [], is_admin: false }));
  }, []);

  const open = (kind, ref) => navigate(`/intelligence?kind=${kind}&ref=${encodeURIComponent(ref)}`);

  const groups = access?.groups || [];
  const clients = access?.clients || [];
  const hasAny = groups.length + clients.length > 0;

  return (
    <Layout>
      <div data-testid="client-home-page">
        <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Asset Intelligence</p>
        <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">
          Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
        </h1>
        <p className="mt-2 max-w-2xl text-base text-neutral-600">
          Select a portfolio below to open its live Asset Intelligence report.
        </p>

        {access === null ? (
          <div className="mt-10 flex h-40 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
        ) : !hasAny ? (
          <div className="mt-8 flex flex-col items-center justify-center gap-2 border border-dashed border-neutral-300 rounded-sm py-16 text-neutral-500" data-testid="no-access">
            <WarningCircle size={28} className="text-[#D97706]" />
            <p className="text-base">No portfolios have been assigned to your login yet.</p>
            <p className="text-sm">Please contact your administrator to get access.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-8">
            {groups.length > 0 && (
              <div>
                <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><UsersThree size={20} weight="bold" /> Client Groups</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {groups.map((g) => (
                    <button key={g.group_id} data-testid={`home-group-${g.group_id}`} onClick={() => open("group", g.group_id)}
                      className="group flex flex-col items-start gap-3 border border-neutral-200 bg-white p-5 text-left shadow-sm rounded-sm transition-all duration-200 hover:border-[#0055FF] hover:shadow-md">
                      <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0A0A0A] text-white"><UsersThree size={22} weight="fill" /></div>
                      <div>
                        <p className="font-heading text-lg font-black tracking-tight text-[#0A0A0A]">{g.name}</p>
                        <p className="text-sm text-neutral-500">{g.client_count} client{g.client_count === 1 ? "" : "s"}</p>
                      </div>
                      <span className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-[#0055FF]">
                        <Brain size={15} weight="bold" /> Open report <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {clients.length > 0 && (
              <div>
                <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-[#0A0A0A]"><Buildings size={20} weight="bold" /> Clients</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {clients.map((c) => (
                    <button key={c.id} data-testid={`home-client-${c.id}`} onClick={() => open("client", c.id)}
                      className="group flex flex-col items-start gap-3 border border-neutral-200 bg-white p-5 text-left shadow-sm rounded-sm transition-all duration-200 hover:border-[#0055FF] hover:shadow-md">
                      <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0055FF] text-white"><Buildings size={22} weight="fill" /></div>
                      <p className="font-heading text-lg font-black tracking-tight text-[#0A0A0A]">{c.name}</p>
                      <span className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-[#0055FF]">
                        <Brain size={15} weight="bold" /> Open report <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
